import { DateRangeSpecialValue } from "@components/inputs/date/popup/Calendar.utils";
import {
    ifAll,
    IFieldDef,
    IGetValueArgs,
    not,
    TGetValueFn,
    TTemporalDialogSettings
} from "@components/smart/FieldInfo";
import {
    countryDef,
    CZPostalCodePlaceholder,
    czPostalCodeValidator,
    getAddressFields,
    getAddressGroup,
    getPostalCodeDef,
    isPostalCodeOptionalForCountry,
    NoteFieldDef,
    postalCodeFormatter
} from "@components/smart/GeneralFieldDefinition";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    AddressEntity,
    EntitySetName,
    EntityTypeName,
    IPrIllnessLeaveEventEntity,
    PrIllnessLeaveEventEntity,
    PrIllnessLeaveEventPermittedOutingEntity,
    PrIllnessLeaveEventResidenceAddressEntity,
    PrSocialEventEntity,
    PrSocialEventMonthDetailEntity
} from "@odata/GeneratedEntityTypes";
import { CountryCode, PrSocialEventTypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { getTemporalPropertyExtraFieldContentAfter, isTemporalFieldReadOnly } from "@odata/TemporalUtils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import {
    CurrentCityLocalPath,
    CurrentCountryLocalPath,
    CurrentLocalAddressFields,
    CurrentPostalCodeLocalPath,
    CurrentStreetLocalPath,
    hasAdditionalFlagsLocalPath,
    hasDoctorLocalPath,
    hasResidenceAddressLocalPath,
    isDoctorAddressFilled
} from "@pages/payroll/socialEvents/eventTypes/IllnessLeave.utils";
import IllnessLeaveFormView from "@pages/payroll/socialEvents/eventTypes/IllnessLeaveFormView";
import { SocialEventGroup } from "@pages/payroll/socialEvents/SocialEvents.types";
import {
    dateEventEndLocalPath,
    getMonthIntervalDayStorage,
    getOtherPropertyGetter,
    workedHoursOnLastDayLocalPath
} from "@pages/payroll/socialEvents/SocialEvents.utils";
import { isNewEntity } from "@utils/definition.utils";
import { forEachKey } from "@utils/general";
import { removeWhiteSpace } from "@utils/string";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import { ValidationError } from "yup";

import { EMPTY_DASH } from "../../../../constants";
import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import {
    BasicInputSizes,
    FastEntryInputSizes,
    FieldType,
    GroupedField,
    LabelStatus,
    ValidatorType
} from "../../../../enums";
import { TValue } from "../../../../global.types";
import { DATE_MAX, DATE_MIN, getUtcDayjs } from "../../../../types/Date";
import {
    addFormAdditionalProperty,
    addFormDef,
    addFormGroup,
    getFormGroup
} from "../../../../views/formView/Form.utils";
import { FormStorage } from "../../../../views/formView/FormStorage";
import {
    addBasicMonthDetailDefinitions,
    addReadOnlyForEventsWithCloseButton,
    addRequiredActionSummaryDef,
    addSubmissionTableDefinitions,
    basicToggleSwitchDef,
    getDefinitions as commonDef,
    getSocialEventsFormDef
} from "../SocialEventsDef";

const isDoctorVisible: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(hasDoctorLocalPath);
};

const isResidenceAddressVisible: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(hasResidenceAddressLocalPath);
};

const isAdditionalFlagsVisible: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(hasAdditionalFlagsLocalPath);
};

const residenceAddressExtraFieldContentAfter = () => {
    return (args: IGetValueArgs) => {
        // don't show when creating new item or readOnly
        if (args.bindingContext.getRootParent().isNew() || (args.storage as FormStorage).isReadOnly) {
            return null;
        }

        return getTemporalPropertyExtraFieldContentAfter(PrIllnessLeaveEventEntity.ResidenceAddresses, AddressEntity.Country)(args);
    };
};

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const d = cloneDeep(getSocialEventsFormDef(IllnessLeaveFormView, PrSocialEventTypeCode.IllnessLeave, context, {
        optionalAttachments: true,
    }));

    addRequiredActionSummaryDef(d);
    addReadOnlyForEventsWithCloseButton(d);

    d.form.customHeaderActions.push({
        id: PrSubmissionTypeCode.ReportingByTheEmployerUponTerminationOfIncapacityForWork,
        label: i18next.t("SocialEvents:Form.CreateHZUPN"),
        iconName: "HZUPN",
        isVisible: not(isNewEntity),
        isDisabled: (args) => {
            const storage = args.storage as FormStorage<IPrIllnessLeaveEventEntity>;
            const { DateEventStart, DateEventEnd } = storage.data.entity;
            // disabled if event is not completed or lasts less than 14 days
            // SocialEventStatusCode !== PrSocialEventStatusCode.Completed ||
            return !DateEventEnd || getUtcDayjs(DateEventEnd).diff(DateEventStart, "days") < 14;
        }
    });

    const typeIdx = d.form.summary.findIndex(i => i.id === PrSocialEventEntity.SocialEventTypeCode);
    const insertTo = typeIdx !== -1 ? typeIdx + 1 : d.form.summary.length;
    d.form.summary.splice(insertTo, 0, {
        id: BindingContext.localContext(PrIllnessLeaveEventEntity.DateEventStart),
        additionalProperties: [
            { id: PrIllnessLeaveEventEntity.DateEventStart },
            { id: PrIllnessLeaveEventEntity.DateEventEnd }
        ],
        label: i18next.t("SocialEvents:Form.EventDates"),
        formatter: (val: TValue, args) => {
            const { DateEventStart, DateEventEnd } = (args.storage as FormStorage<IPrIllnessLeaveEventEntity>).data.entity;
            if (DateEventStart && (DateEventEnd || getUtcDayjs().isAfter(DateEventStart))) {
                // +1 to include the start day in the count
                const count = getUtcDayjs(DateEventEnd).diff(DateEventStart, "days") + 1;
                return i18next.t("Common:General.XDays", { count });
            }
            return EMPTY_DASH;
        },
    });
    addFormAdditionalProperty(d, { id: PrIllnessLeaveEventEntity.IsCancelled });

    // ************* Month Details and DateEventStart + End *************
    addBasicMonthDetailDefinitions(d);
    // add DateEventContinues to MonthDetails
    addFormDef(d, createPath(SocialEventGroup.MonthDetails, PrSocialEventMonthDetailEntity.DateEventContinues), {
        label: i18next.t("SocialEvents:Form.DateEventContinues"),
        fieldSettings: {
            minDate: ({ storage, bindingContext }) => {
                const monthDate = getMonthIntervalDayStorage(storage, bindingContext.getParent());
                const startDate = storage.getValueByPath(PrIllnessLeaveEventEntity.DateEventStart);
                if (monthDate && monthDate.isAfter(startDate, "day")) {
                    return monthDate.toDate();
                }
                return startDate;
            },
            maxDate: ({ storage, bindingContext }) => {
                const monthDate = getMonthIntervalDayStorage(storage, bindingContext.getParent());
                return monthDate.endOf("month").toDate();
            },
        }
    });
    const monthDetailsGroup = getFormGroup(d, SocialEventGroup.MonthDetails);
    monthDetailsGroup.lineItems.columns.push({ id: PrSocialEventMonthDetailEntity.DateEventContinues });
    // add EndEvent fields to General group
    const generalGroup = getFormGroup(d, SocialEventGroup.General);
    generalGroup.rows[generalGroup.rows.length - 1].push(
        { id: dateEventEndLocalPath },
        { id: workedHoursOnLastDayLocalPath },
    );

    // ************* Doctor *************
    addFormDef(d, PrIllnessLeaveEventEntity.HealthcareProviderName, {
        width: BasicInputSizes.L,
        label: i18next.t("SocialEvents:Form.HealthcareProviderName"),
        groupedField: GroupedField.WrappedMultiStart,
        isVisible: isDoctorVisible,
    });
    addFormDef(d, PrIllnessLeaveEventEntity.DoctorName, {
        width: BasicInputSizes.L,
        groupedField: GroupedField.WrappedMultiEnd,
        isVisible: isDoctorVisible,
        collapsedRows: [
            ...getAddressGroup(PrIllnessLeaveEventEntity.DoctorAddress)
        ]
    });
    const doctorAddressFields = getAddressFields(PrIllnessLeaveEventEntity.DoctorAddress);
    const docAffectedFields: IFieldDef[] = Object.keys(doctorAddressFields).map(id => ({ id }));
    forEachKey(doctorAddressFields, fieldId => {
        addFormDef(d, fieldId, {
            ...doctorAddressFields[fieldId],
            isVisible: isDoctorVisible,
            isRequired: ifAll(doctorAddressFields[fieldId].isRequired, isDoctorAddressFilled, isDoctorVisible),
            affectedFields: [...docAffectedFields]
        });
    });
    addFormDef(d, hasDoctorLocalPath, {
        ...basicToggleSwitchDef,
        affectedFields: [
            { id: PrIllnessLeaveEventEntity.HealthcareProviderName },
            { id: PrIllnessLeaveEventEntity.DoctorName },
            ...docAffectedFields
        ]
    });
    addFormGroup(d, {
        id: SocialEventGroup.Doctor,
        togglePropPath: hasDoctorLocalPath,
        title: i18next.t("SocialEvents:Form.Doctor"),
        rows: [
            [
                { id: PrIllnessLeaveEventEntity.HealthcareProviderName },
                { id: PrIllnessLeaveEventEntity.DoctorName },
            ],
        ],
    });
    // ************* Residence addresses *************

    // temporal property bag days from/to definitions
    const AddressDateFromPath = createPath(PrIllnessLeaveEventEntity.ResidenceAddresses, PrIllnessLeaveEventResidenceAddressEntity.DateValidFrom);
    const AddressDateToPath = createPath(PrIllnessLeaveEventEntity.ResidenceAddresses, PrIllnessLeaveEventResidenceAddressEntity.DateValidTo);
    addFormDef(d, AddressDateFromPath, {
        defaultValue: DATE_MIN,
        isReadOnly: isTemporalFieldReadOnly,
        width: FastEntryInputSizes.M,
        useForValidation: true,
        fieldSettings: {
            showSpecialValue: DateRangeSpecialValue.WithoutStart
        },
    });
    addFormDef(d, AddressDateToPath, {
        defaultValue: DATE_MAX,
        isReadOnly: isTemporalFieldReadOnly,
        width: FastEntryInputSizes.M,
        useForValidation: true,
        fieldSettings: {
            showSpecialValue: DateRangeSpecialValue.WithoutEnd
        },
    });

    const residenceFields = getAddressFields(PrIllnessLeaveEventEntity.ResidenceAddresses);
    const removePrefix = (fieldId: string) => fieldId.replace(`${PrIllnessLeaveEventEntity.ResidenceAddresses}/`, "");

    const residenceAddressTemporalDialogSettings: TTemporalDialogSettings = {
        dialogTitle: i18next.t("SocialEvents:Form.ResidenceAddresses"),
        columns: [
            // without prefix
            ...Object.keys(residenceFields).map(fieldId => removePrefix(fieldId))
        ],
        granularity: "day",
        withoutGaps: true,
    };

    const isCurrentPostalCodeRequired = (args: IGetValueArgs): boolean => {
        const { storage } = args;
        const country = storage.getValueByPath(CurrentCountryLocalPath);
        return !isPostalCodeOptionalForCountry(country);
    };
    const currentPostalCodeValidator = (value: TValue, args: IGetValueArgs): boolean => {
        if (isCurrentPostalCodeRequired(args)) {
            return czPostalCodeValidator(value);
        }
        return true;
    };

    // addFormDef(d, PrIllnessLeaveEventEntity.ResidenceAddresses, {}); // collection is needed here to properly handle temporal dialog??
    forEachKey(residenceFields, fieldId => {
        const isRequired = residenceFields[fieldId].isRequired ? residenceFields[fieldId].isRequired : true;
        // definition for temporal dialog
        addFormDef(d, fieldId, {
            ...residenceFields[fieldId],
            isRequired,
            fieldSettings: {
                ...residenceFields[fieldId].fieldSettings,
                temporalDialog: residenceAddressTemporalDialogSettings
            }
        });
    });
    // current field definition
    addFormDef(d, CurrentStreetLocalPath, {
        label: i18next.t("SocialEvents:Form.Street"),
        groupedField: GroupedField.WrappedMultiStart,
        isRequired: true,
        useForValidation: true,
        width: BasicInputSizes.L,
        isVisible: isResidenceAddressVisible,
        additionalProperties: [
            { id: `/${createPath(PrIllnessLeaveEventEntity.ResidenceAddresses, AddressEntity.Street)}` },
            { id: `/${AddressDateFromPath}` },
            { id: `/${AddressDateToPath}` }
        ],
    });
    addFormDef(d, CurrentCityLocalPath, {
        label: i18next.t("SocialEvents:Form.City"),
        isRequired: true,
        useForValidation: true,
        isVisible: isResidenceAddressVisible,
        additionalProperties: [
            { id: `/${createPath(PrIllnessLeaveEventEntity.ResidenceAddresses, AddressEntity.City)}` },
        ],
    });
    addFormDef(d, CurrentPostalCodeLocalPath, {
        ...getPostalCodeDef(),
        label: i18next.t("SocialEvents:Form.PostalCode"),
        isRequired: isCurrentPostalCodeRequired,
        useForValidation: true,
        isVisible: isResidenceAddressVisible,
        additionalProperties: [
            { id: `/${createPath(PrIllnessLeaveEventEntity.ResidenceAddresses, AddressEntity.PostalCode)}` },
        ],
        validator: {
            type: ValidatorType.Custom,
            settings: {
                customValidator: currentPostalCodeValidator,
                message: i18next.t("Common:Validation.PostalCode")
            }
        },
        formatter: (val, args) => {
            const countryCode = args.storage.getValueByPath(CurrentCountryLocalPath) as CountryCode;
            return postalCodeFormatter(val as string, countryCode);
        },
        parser: (val) => {
            return removeWhiteSpace(val);
        },
        isValid: currentPostalCodeValidator,
        fieldSettings: {
            placeholder: (args) => {
                const isRequired = isCurrentPostalCodeRequired(args);
                return isRequired ? CZPostalCodePlaceholder : "";
            }
        }
    });
    addFormDef(d, CurrentCountryLocalPath, {
        ...countryDef,
        label: i18next.t("SocialEvents:Form.Country"),
        groupedField: GroupedField.WrappedMultiEnd,
        isRequired: true,
        useForValidation: true,
        isVisible: isResidenceAddressVisible,
        extraFieldContentAfter: residenceAddressExtraFieldContentAfter(),
        additionalProperties: [
            { id: `/${createPath(PrIllnessLeaveEventEntity.ResidenceAddresses, AddressEntity.Country)}` },
        ],
        fieldSettings: {
            ...countryDef.fieldSettings,
            entitySet: EntitySetName.Countries,
            localDependentFields: []
        },
        affectedFields: [
            { id: CurrentPostalCodeLocalPath, navigateFromParent: true, revalidate: true }
        ],
    });

    addFormDef(d, hasResidenceAddressLocalPath, {
        ...basicToggleSwitchDef,
        affectedFields: CurrentLocalAddressFields.map(fieldId => ({ id: fieldId }))
    });

    addFormGroup(d, {
        id: SocialEventGroup.ResidenceAddresses,
        title: i18next.t("SocialEvents:Form.ResidenceAddresses"),
        togglePropPath: hasResidenceAddressLocalPath,
        rows: [
            CurrentLocalAddressFields.map(field => ({ id: field }))
        ]
    });

    // ************* Permitted outings *************
    addFormDef(d, createPath(PrIllnessLeaveEventEntity.PermittedOutings, PrIllnessLeaveEventPermittedOutingEntity.DateValidFrom), {
        defaultValue: DATE_MIN,
        fieldSettings: {
            showSpecialValue: DateRangeSpecialValue.WithoutStart
        },
        affectedFields: [
            { id: PrIllnessLeaveEventPermittedOutingEntity.DateValidTo, navigateFromParent: true, revalidate: true }
        ]
    });
    addFormDef(d, createPath(PrIllnessLeaveEventEntity.PermittedOutings, PrIllnessLeaveEventPermittedOutingEntity.DateValidTo), {
        defaultValue: DATE_MAX,
        fieldSettings: {
            showSpecialValue: DateRangeSpecialValue.WithoutEnd,
            minDate: getOtherPropertyGetter(PrIllnessLeaveEventPermittedOutingEntity.DateValidFrom)
        }
    });
    addFormDef(d, createPath(PrIllnessLeaveEventEntity.PermittedOutings, PrIllnessLeaveEventPermittedOutingEntity.TimeFrom), {
        type: FieldType.Time,
        width: BasicInputSizes.XS,
        affectedFields: [
            { id: PrIllnessLeaveEventPermittedOutingEntity.TimeTo, navigateFromParent: true, revalidate: true }
        ]
    });
    addFormDef(d, createPath(PrIllnessLeaveEventEntity.PermittedOutings, PrIllnessLeaveEventPermittedOutingEntity.TimeTo), {
        type: FieldType.Time,
        width: BasicInputSizes.XS,
        validator: {
            type: ValidatorType.Date,
            settings: {
                customValidator: (value, args) => {
                    const { storage, bindingContext } = args;
                    const timeFrom = storage.getValue(bindingContext.getParent().navigate(PrIllnessLeaveEventPermittedOutingEntity.TimeFrom));
                    const from = timeFrom && getUtcDayjs(timeFrom as Date);
                    const to = value && getUtcDayjs(value as Date);
                    if (from && to && (from.get("hour") > to.get("hour") || (from.get("hour") === to.get("hour") && from.get("minute") > to.get("minute")))) {
                        return new ValidationError(i18next.t("SocialEvents:Form.PermittedOutingsTimeToBeforeFrom"), value, args.bindingContext.getEntityPath(false));
                    }
                    return true;
                }
            }
        }
    });

    addFormGroup(d, {
        id: SocialEventGroup.PermittedOutings,
        title: i18next.t("SocialEvents:Form.PermittedOutings"),
        lineItems: {
            collection: PrIllnessLeaveEventEntity.PermittedOutings,
            columns: [
                { id: PrIllnessLeaveEventPermittedOutingEntity.DateValidFrom },
                { id: PrIllnessLeaveEventPermittedOutingEntity.DateValidTo },
                { id: PrIllnessLeaveEventPermittedOutingEntity.TimeFrom },
                { id: PrIllnessLeaveEventPermittedOutingEntity.TimeTo }
            ],
            isGroupToggleable: true,
            isTheOnlyItemRemovable: false,
            canReorder: false,
            isItemCloneable: true,
            order: PrIllnessLeaveEventPermittedOutingEntity.Id,
        }
    });

    // ************* AdditionalFlags *************
    const additionalFlagsCheckboxGroupLocalPath = BindingContext.localContext("additionalFlagsCheckboxGroup");
    const groupedCheckboxFields = [
        PrIllnessLeaveEventEntity.IsWorkplaceInjury,
        PrIllnessLeaveEventEntity.IsCausedByAnotherPerson,
        PrIllnessLeaveEventEntity.IsCausedByAlcoholOrDrugs
    ];
    addFormDef(d, additionalFlagsCheckboxGroupLocalPath, {
        type: FieldType.CheckboxGroup,
        labelStatus: LabelStatus.Removed,
        isVisible: isAdditionalFlagsVisible,
        width: "100%",
        fieldSettings: {
            items: groupedCheckboxFields.map(id => ({
                id,
                label: i18next.t(`SocialEvents:Form.${id}`)
            }))
        },
        additionalProperties: groupedCheckboxFields.map(fieldId => ({ id: `/${fieldId}` }))
    });
    addFormDef(d, hasAdditionalFlagsLocalPath, {
        ...basicToggleSwitchDef,
        affectedFields: [
            { id: additionalFlagsCheckboxGroupLocalPath },
        ]
    });
    addFormGroup(d, {
        id: SocialEventGroup.AdditionalFlags,
        togglePropPath: hasAdditionalFlagsLocalPath,
        title: i18next.t("SocialEvents:Form.AdditionalFlags"),
        rows: [[
            { id: additionalFlagsCheckboxGroupLocalPath }
        ]]
    });

    // ************* Optional fields addable via configuration *************
    addFormDef(d, PrIllnessLeaveEventEntity.Note, {
        ...NoteFieldDef
    });
    addFormDef(d, PrIllnessLeaveEventEntity.NotificationId, {
    });

    // Table tabs on the end of the form
    addSubmissionTableDefinitions(d);

    return {
        ...d,
        entitySet: EntitySetName.PrIllnessLeaveEvents
    };
};

getDefinitions.translationFiles = commonDef.translationFiles;
setDefByEntityType(EntityTypeName.PrIllnessLeaveEvent, getDefinitions);